import { memo, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import NavigationItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/NavigationItem';
import Navigation from '@wconcept/wconcept-ui-renewal/src/components/molecules/_indicator/Navigation';
import type {
  PromotionBasicNavigationTab,
  PromotionContent,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import useGa4Event from '@hooks/useGa4Event';
import { useScrollPositionNavi } from '@wconcept/wconcept-ui-renewal/src/hooks/useScrollPositionNavi';
import usePromotionVisibilityStore from '@store/promotionVisibilityStore';
import useScrollStore from '@store/scrollStore';
import { useQueryClient } from '@tanstack/react-query';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import {
  dispatchProgrammaticScrollEvent,
  isPromotionSectionScrollRequestActive,
  startPromotionSectionScrollRequest,
  usePromotionSectionPosition,
} from '@hooks/usePromotionSectionPosition';
import { getPromotionContentIdsThroughOrder } from '@hooks/useVisibleModules';
import { resolveBasicNavigationSelectedValue } from './basicNavigationSelection';

const PRODUCT_LOADING_POLL_INTERVAL_MS = 100;
const PRODUCT_LOADING_TIMEOUT_MS = 5000;

const waitForNextPaint = (): Promise<void> =>
  new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

interface BasicNavigationModuleProps {
  readonly bgType?: string;
  readonly visualTokens: PromotionModuleVisualTokens;
  readonly naviData: readonly PromotionBasicNavigationTab[];
  readonly promotionContents: readonly PromotionContent[];
  readonly isDesktop: boolean;
  readonly activeColor: string;
}

function BasicNavigationModule({
  bgType,
  visualTokens,
  naviData,
  promotionContents,
  isDesktop,
  activeColor,
}: BasicNavigationModuleProps) {
  const { sendGa4TabClick } = useGa4Event();

  // React Query 클라이언트로 실시간 쿼리 상태 확인
  const queryClient = useQueryClient();
  const { scrollToElement, stopScrollAdjustment } = usePromotionSectionPosition(
    { isDesktop }
  );
  const navigationRef = useRef<HTMLDivElement>(null);

  const getScrollActivationOffset = useCallback((): number => {
    const navigation = navigationRef.current;
    if (
      !navigation ||
      window.getComputedStyle(navigation).position !== 'fixed'
    ) {
      return 0;
    }

    return Math.max(navigation.getBoundingClientRect().bottom, 0);
  }, []);

  // 스크롤 상태 스토어 가져오기
  const scrollStore = useScrollStore(state => ({
    manualScroll: state.manualScroll,
    pendingNaviClick: state.pendingNaviClick,
    setManualScroll: state.setManualScroll,
    setPendingNaviClick: state.setPendingNaviClick,
  }));

  const { activeSection, setActiveSection, handleNaviClick } =
    useScrollPositionNavi(
      naviData.map(v => v.anchor),
      naviData[0]?.anchor,
      {
        scrollStore,
        offset: getScrollActivationOffset,
      }
    );

  const waitForPreviousProductsToRender = async (
    sectionNumber: number
  ): Promise<void> => {
    const targetContentIds = getPromotionContentIdsThroughOrder(
      promotionContents,
      sectionNumber
    );

    // pendingNaviClick 반영으로 대상 이전 상품 쿼리가 시작된 다음 상태를 확인한다.
    await waitForNextPaint();

    await new Promise<void>(resolve => {
      const startedAt = Date.now();

      const checkLoading = (): void => {
        const queryStatusMap =
          usePromotionVisibilityStore.getState().accumulatedQueryStatus;
        const isStoredQueryLoading = Array.from(targetContentIds).some(
          contentId => {
            const queryStatus = queryStatusMap.get(contentId);
            return queryStatus?.isLoading || queryStatus?.isFetching;
          }
        );
        const isTargetQueryFetching =
          queryClient.isFetching({
            predicate: query =>
              query.queryKey.some(key => targetContentIds.has(String(key))),
          }) > 0;
        const isRecommendQueryFetching =
          queryClient.isFetching({
            queryKey: ['promotionProductsRecommend'],
          }) > 0;
        const didTimeout = Date.now() - startedAt >= PRODUCT_LOADING_TIMEOUT_MS;

        if (
          didTimeout ||
          (!isStoredQueryLoading &&
            !isTargetQueryFetching &&
            !isRecommendQueryFetching)
        ) {
          resolve();
          return;
        }

        setTimeout(checkLoading, PRODUCT_LOADING_POLL_INTERVAL_MS);
      };

      checkLoading();
    });

    await waitForNextPaint();
  };

  const handleClick = async (sectionNumber: number): Promise<void> => {
    const requestId = startPromotionSectionScrollRequest();
    stopScrollAdjustment();
    flushSync(() => {
      setActiveSection(sectionNumber);
    });
    handleNaviClick(sectionNumber);

    const element = document.getElementById(`section${sectionNumber}`);
    if (element) {
      dispatchProgrammaticScrollEvent(
        window,
        element.getBoundingClientRect().top + window.pageYOffset
      );
      await waitForPreviousProductsToRender(sectionNumber);
      if (!isPromotionSectionScrollRequestActive(requestId)) return;
      await scrollToElement(element, () =>
        isPromotionSectionScrollRequestActive(requestId)
      );
    }
  };

  const onClickNavigation = (v: {
    tabName: string;
    order: number;
    anchor: number;
  }): void => {
    sendGa4TabClick({
      text: v.tabName,
      area: 'promotion_navigation_tab',
      listNo: v.order,
      page: 'promotion',
    });
  };

  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
    pointColorFallback: activeColor,
  });

  const navigationColorType = customColorsFull ?? presetBgType;
  const selectedValue = resolveBasicNavigationSelectedValue(
    activeSection,
    scrollStore.pendingNaviClick
  );

  return (
    <Navigation
      ref={navigationRef}
      selectedValue={selectedValue}
      colorType={navigationColorType}
      items={naviData?.map((v, i) => (
        <NavigationItem
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          value={v.anchor}
          text={v.tabName}
          onClick={() => {
            onClickNavigation(v);
            handleClick(v.anchor);
          }}
        />
      ))}
      isDesktop={isDesktop}
    />
  );
}

export default memo(BasicNavigationModule);
